import { supabase } from '@/lib/supabase';
import { optimizeImage, type ImageKind } from '@/lib/imagePipeline';
import type { ThemeAsset } from '@/types';

/**
 * Theme asset uploads: optimize client-side (WebP, capped dimensions), dedupe
 * by content hash, upload to the owner's folder in `theme-assets`, record
 * metadata. Returns the public URL documents reference — bytes never enter a
 * row or a doc.
 */
export interface UploadedAsset {
  asset: ThemeAsset;
  url: string;
}

export async function uploadThemeAsset(
  file: File | Blob,
  kind: Extract<ImageKind, 'background' | 'banner'>,
  ownerId: string,
): Promise<UploadedAsset> {
  const optimized = await optimizeImage(file, kind);

  // Same bytes already uploaded by this user → reuse the existing object.
  const { data: existing } = await supabase
    .from('theme_assets')
    .select('*')
    .eq('owner_id', ownerId)
    .eq('content_hash', optimized.contentHash)
    .limit(1)
    .maybeSingle();
  if (existing) return { asset: existing, url: publicAssetUrl(existing.path) };

  const id = crypto.randomUUID();
  const path = `${ownerId}/${id}.${optimized.ext}`;
  const { error: uploadError } = await supabase.storage
    .from('theme-assets')
    .upload(path, optimized.blob, { contentType: 'image/webp', cacheControl: '31536000' });
  if (uploadError) throw uploadError;

  const { data: asset, error } = await supabase
    .from('theme_assets')
    .insert({
      id,
      owner_id: ownerId,
      bucket: 'theme-assets',
      path,
      kind,
      width: optimized.width,
      height: optimized.height,
      bytes: optimized.bytes,
      content_hash: optimized.contentHash,
    })
    .select('*')
    .single();
  if (error) throw error;

  return { asset, url: publicAssetUrl(path) };
}

export function publicAssetUrl(path: string): string {
  return supabase.storage.from('theme-assets').getPublicUrl(path).data.publicUrl;
}
